/**
 * Canvas — Mockup canvas with drag/zoom and image compositing.
 * Replaces window.MockupCanvas and CanvasRenderer.
 */
import { useRef, useState, useEffect, type FC } from 'react';
import {
  normalizeTransform,
  applyPrintTransform,
  applyMask,
  applyOverlay,
} from '../services/renderService';
import { loadImage } from '../utils';
import type { Transform } from '../stores/types';

interface Product {
  id: string;
  image?: string;
  mask?: string;
  overlay?: string;
  renderer?: string;
  blendMode?: string;
  width?: number;
  height?: number;
}

interface Props {
  product: Product;
  imageUrl: string | null;
  maskUrl?: string;
  overlayUrl?: string;
  transform: Transform;
  onUpdateTransform: (t: Transform) => void;
  isActive: boolean;
  onActivate?: () => void;
  maskColor: string | null;
  productId?: string;
  dpi?: number;
}

export const Canvas: FC<Props> = ({
  product,
  imageUrl,
  maskUrl,
  overlayUrl,
  transform,
  onUpdateTransform,
  isActive,
  onActivate,
  maskColor,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    startX: number;
    startY: number;
  } | null>(null);

  const t = normalizeTransform(transform);

  const [images, setImages] = useState<{
    base: HTMLImageElement | null;
    mask: HTMLImageElement | null;
    overlay: HTMLImageElement | null;
    print: HTMLImageElement | null;
  }>({ base: null, mask: null, overlay: null, print: null });

  // Load images
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [baseImg, maskImg, overlayImg, printImg] = await Promise.all([
          loadImage(product.image),
          loadImage(maskUrl ?? product.mask),
          loadImage(overlayUrl ?? product.overlay),
          loadImage(imageUrl),
        ]);
        if (mounted)
          setImages({ base: baseImg, mask: maskImg, overlay: overlayImg, print: printImg });
      } catch (err) {
        console.error('Image load error:', err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [product.image, maskUrl, product.mask, overlayUrl, product.overlay, imageUrl]);

  // Draw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const { base: baseImg, mask: maskImg, overlay: overlayImg, print: printImg } = images;
    if (!baseImg && !printImg) return;

    let rafId: number;
    const draw = () => {
      canvas.width = baseImg ? baseImg.width : product.width || 1000;
      canvas.height = baseImg ? baseImg.height : product.height || 1000;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';

      // Checkerboard
      const cs = 20;
      for (let y = 0; y < canvas.height; y += cs) {
        for (let x = 0; x < canvas.width; x += cs) {
          ctx.fillStyle = (x / cs + y / cs) % 2 === 0 ? '#e5e7eb' : '#ffffff';
          ctx.fillRect(x, y, cs, cs);
        }
      }

      if (baseImg) ctx.drawImage(baseImg, 0, 0);

      if (printImg) {
        const tmp = document.createElement('canvas');
        tmp.width = canvas.width;
        tmp.height = canvas.height;
        const tCtx = tmp.getContext('2d')!;
        tCtx.clearRect(0, 0, canvas.width, canvas.height);

        applyPrintTransform(tCtx, printImg, t, canvas.width, canvas.height);
        applyMask(tCtx, maskImg, canvas.width, canvas.height);

        ctx.save();
        ctx.globalCompositeOperation =
          (product.blendMode as GlobalCompositeOperation) || 'source-over';
        ctx.drawImage(tmp, 0, 0);

        if (maskColor && maskImg) {
          const mo = document.createElement('canvas');
          mo.width = canvas.width;
          mo.height = canvas.height;
          const mCtx = mo.getContext('2d')!;
          mCtx.fillStyle = maskColor;
          mCtx.fillRect(0, 0, canvas.width, canvas.height);
          mCtx.globalCompositeOperation = 'destination-out';
          mCtx.drawImage(maskImg, 0, 0, canvas.width, canvas.height);
          ctx.globalAlpha = 0.85;
          ctx.drawImage(mo, 0, 0);
          ctx.globalAlpha = 1.0;
        }
        ctx.restore();
      }

      if (overlayImg) applyOverlay(ctx, overlayImg, canvas.width, canvas.height);
    };

    rafId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafId);
  }, [
    images,
    t.x,
    t.y,
    t.scale,
    t.rotation,
    product.width,
    product.height,
    maskColor,
    product.blendMode,
  ]);

  // Drag refs
  const stateRef = useRef({ t, onUpdateTransform, imageUrl, isDragging });
  useEffect(() => {
    stateRef.current = { t, onUpdateTransform, imageUrl, isDragging };
  }, [t, onUpdateTransform, imageUrl, isDragging]);

  // Mouse drag on document
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { t: ct, onUpdateTransform: onUT, imageUrl: img } = stateRef.current;
      if (!img || !dragStartRef.current) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dx = e.clientX - dragStartRef.current.mouseX;
      const dy = e.clientY - dragStartRef.current.mouseY;
      onUT({
        ...ct,
        x: dragStartRef.current.startX + dx * (canvas.width / rect.width),
        y: dragStartRef.current.startY + dy * (canvas.height / rect.height),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleMouseDown = (e: React.MouseEvent) => {
    onActivate?.();
    if (!imageUrl) return;
    setIsDragging(true);
    dragStartRef.current = { mouseX: e.clientX, mouseY: e.clientY, startX: t.x, startY: t.y };
  };

  // Wheel zoom
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      onActivate?.();
      e.preventDefault();
      const { t: ct, onUpdateTransform: onUT, imageUrl: img } = stateRef.current;
      if (!img) return;
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      onUT({ ...ct, scale: Math.min(10, Math.max(0.05, ct.scale + delta)) });
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`w-full h-full flex flex-col bg-slate-900/50 rounded-xl overflow-hidden border p-2 pb-6 select-none no-scroll-zoom transition-colors duration-200 ${
        isActive
          ? 'border-indigo-500 ring-1 ring-indigo-500/50'
          : 'border-slate-700 hover:border-slate-600'
      }`}
      onClick={() => onActivate?.()}
      title="Перетаскивайте для перемещения. Ctrl + Скролл для масштабирования."
    >
      <div className="relative flex-1 flex items-center justify-center">
        <canvas
          ref={canvasRef}
          className="max-h-full max-w-full shadow-2xl cursor-move object-contain"
          onMouseDown={handleMouseDown}
        />
      </div>
    </div>
  );
};

export { Canvas as MockupCanvas };
